// src/lib/useDiseaseRanking.ts
// 疾患深掘りタブ用 hook: /api/query/disease_ranking を期間 + filter (dep / dpcfy / dpc6 / admdrcd)
// で呼ぶ TanStack Query フック。 期間は asOfMonth 起点で直近 12 ヶ月 (月初〜月末) を採用。
import { useQuery } from "@tanstack/react-query";
import { useFilters, effectiveDpcSelection } from "../store";
import type { DiseaseRankingResponse } from "@shared/api";
import { GP_MODE_HEADER } from "./gpMode";

import { diseasePeriod } from "./diseasePeriod";
export { diseasePeriod } from "./diseasePeriod";

export function useDiseaseRanking() {
  const f = useFilters();
  // 期間: 通常は asOfMonth から直近 12 ヶ月。
  // ユーザが sidebar で from / to を override したらそれを優先。
  const { dateFrom: defFrom, dateTo: defTo } = diseasePeriod(f.asOfMonth, f.dpcfy, f.diseaseAutoPeriod);
  const dateFrom = f.dateFromOverride ?? defFrom;
  const dateTo   = f.dateToOverride   ?? defTo;
  // 疾患深掘りタブの「診療科」 filter は 入院系の focusedDept を流用。
  // 粒度 (小/中/大) も合わせて server に渡し、 server 側で dashboard 名 →
  // fact_disease_adm.dep の値リストに展開して IN filter する。
  // 病院全体 anchor (focusedDept=null) なら全科。
  const dep = f.focusedDept;
  const granularity = f.granularity;
  const minCases = f.minCases;
  const dpcfy = f.dpcfy;
  // 「合計」 (= filter 解除) は server に null として送り、 SQL 側で OR 条件で素通り
  const yotei = f.diseaseYotei === "合計" ? null : f.diseaseYotei;
  const iryo  = f.diseaseIryo  === "合計" ? null : f.diseaseIryo;
  const level = f.dpcLevel;   // [桁切替] 6=疾患別/10=手術別/11=処置1別/12=処置2別
  // [絞り込み保持 2026-07-17] サイドバー DPC 階層絞り込み (手術分類/処置1分岐/処置2分岐)。
  // 「表示階層以下の絞り込みは上位階層に反映させない」ため、level でゲートした最深 1 本を送る。
  const selDpc = effectiveDpcSelection(f);
  return useQuery({
    queryKey: ["disease_ranking", dateFrom, dateTo, dep, granularity, dpcfy, f.dpc6, selDpc, f.admdrcd, minCases, yotei, iryo, level],
    queryFn: async () => {
      const r = await fetch("/api/query/disease_ranking", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...GP_MODE_HEADER },
        body: JSON.stringify({
          dateFrom, dateTo,
          dep, granularity, dpcfy, dpc6: f.dpc6, selDpc, admdrcd: f.admdrcd,
          minCases,
          yotei, iryo, level,
        }),
      });
      if (!r.ok) throw new Error(`disease_ranking ${r.status}`);
      return r.json() as Promise<DiseaseRankingResponse>;
    },
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
}
